import React, { useEffect, useRef, useState } from "react";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import Checkbox from "@mui/material/Checkbox";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Layout from "../../Components/Layout";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { GET_ALL_PERMISSIONS_ADMIN, GET_ALL_ROLES_V2, UPDATE_ALL_PERMISSIONS_ADMIN } from "../../../utils/services/apiPath";
import { getApi, postApi } from "../../../utils/services";
import { useSelector } from "react-redux";
import { errorToast, successToast } from "../../../utils/toastHelper";
import SettingFormsLayout from "../../Components/ProForms/ProFormsLayout/SettingFormsLayout";
import ProSelectField from "../../Components/Inputs/SelectField";
import { fieldTruePermission } from "../../../utils/formHelper";
import { userRoleSelector } from "../../store/features/auth/authSlice";
import "./UserPermissions.scss";
import { v4 as uuidv4 } from "uuid";

const theme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: "#e9f9ff"
        },
        row: {
          "&:hover": {
            backgroundColor: "transparent"
          }
        }
      }
    }
  }
});

const groupingColDef = {
  headerName: "Name",
  field: "name",
  width: 300,
  minWidth: 300,
  hideDescendantCount: true,
  flex: 1
};

const UserPermissions = () => {
  const [treeData, setTreeData] = useState([]);
  const { control, setValue, watch } = useForm();
  const [loading, setLoading] = useState(true);
  const [rolesData, setRolesData] = useState([]);
  const [isSaveHidden, setIsSaveHidden] = useState(false);
  const userRole = useSelector(userRoleSelector);
  const roleId = watch("roleId");
  const apiRef = useGridApiRef();
  const initialData = useRef([]);
  const getTreeDataPath = useRef(row => row.path || []);

  const getAllRoles = async () => {
    try {
      const data = await getApi(GET_ALL_ROLES_V2);
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error("Invalid or empty response format for roles.");
      }
      setRolesData(data);
      setValue("roleId", data[0]?.value || null);
    } catch (error) {
      errorToast(error.message || "Failed to fetch roles.");
    }
  };
  // const isRoleEditable = (selectedRole) => selectedRole !== userRole;
  const getRolePermission = async () => {
    if (!roleId) return;
    setLoading(true);
    const { data, error } = await postApi(`${GET_ALL_PERMISSIONS_ADMIN}?RoleTypeGuid=${roleId}`);
    setLoading(false);
    if (error) return errorToast(error);
    const formattedData = data.map(item => ({ ...item, id: uuidv4() }));
    apiRef.current.setRows(formattedData);
    setTreeData(formattedData);
  };

  const updateAllPermissionsAdmin = async () => {
    if (!roleId) return;
    setLoading(true);
    const payload = { permissions: treeData.map(per => (per.isMandatory ? { ...per, canAdd: true } : per)), roleTypeId: roleId };
    const { error } = await postApi(UPDATE_ALL_PERMISSIONS_ADMIN, payload);
    setLoading(false);
    if (error) return errorToast(error);
    successToast("Permissions updated successfully!", "success");
    getRolePermission();
  };

  const handleCheckboxChange = (event, params) => {
    const { field, row } = params;
    const { path } = row;
    const lastPathSegment = path[path.length - 1];
    const value = event.target.checked;

    setTreeData(prevData => {
      const updateData = prevData.map(item => {
        return item?.path?.includes(lastPathSegment) ? updateRowAndDescendants(item, field, value, lastPathSegment) : item;
      });
      apiRef.current.updateRows(updateData);
      return updateData;
    });
  };

  const updateRowAndDescendants = (row, field, value, targetPathSegment) => {
    const updatedRow = { ...row, [field]: value };
    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(child => updateRowAndDescendants(child, field, value, targetPathSegment));
    }
    return updatedRow;
  };

  const handleSelectAllChange = (event, field) => {
    const checked = event.target.checked;
    setTreeData(prevData => {
      const updatedData = prevData.map(row => ({
        ...row,
        [field]: checked
      }));
      apiRef.current.updateRows(updatedData);
      return updatedData;
    });
  };

  useEffect(() => {
    if (!roleId) getAllRoles();
  }, []);

  useEffect(() => {
    getRolePermission();
  }, [roleId]);

  useEffect(() => {
    setIsSaveHidden(roleId === userRole);
  }, [roleId, userRole]);

  const renderSelectAllCheckbox = (field, name) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <Checkbox onChange={event => handleSelectAllChange(event, field)} />
        <span style={{ fontSize: "14px" }}>{name}</span>
      </div>
    );
  };

  const renderCheckbox = params => {
    const { field, row, value } = params;
    const isMandatory = "canAdd" === field && row.fieldId && row.isMandatory;
    return <Checkbox disabled={isMandatory} checked={isMandatory || value} onChange={event => handleCheckboxChange(event, params)} />;
  };

  const columns = [
    {
      field: "canView",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canView", "View"),
      renderCell: renderCheckbox,
      editable: false
    },
    {
      field: "canAdd",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canAdd", "Create"),
      renderCell: renderCheckbox,
      editable: false
    },
    {
      field: "canEdit",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canEdit", "Update"),
      renderCell: renderCheckbox,
      editable: false
    },
    {
      field: "canDelete",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canDelete", "Delete"),
      renderCell: renderCheckbox,
      editable: false
    },
    {
      field: "canImport",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canImport", "Import"),
      renderCell: renderCheckbox,
      editable: false
    },
    {
      field: "canExport",
      minWidth: 150,
      headerName: renderSelectAllCheckbox("canExport", "Export"),
      renderCell: renderCheckbox,
      editable: false
    }
  ];

  return (
    <Layout>
      <SettingFormsLayout
        title={`Manage User Permission`}
        backLabel="Go Back to All Labor Classification Title"
        beingEdited={true}
        hideCancel={true}
        hideBackbutton={true}
        hideSave={isSaveHidden}
        onSave={updateAllPermissionsAdmin}
      >
        <Grid container mb={"10px"}>
          <ProSelectField control={control} label="Select User" name={"roleId"} mode={"edit"} options={rolesData} permission={fieldTruePermission} />
        </Grid>
        <div className="userPermissionWrapper">
          <ThemeProvider theme={theme}>
            <DataGridPro
              loading={loading}
              rows={initialData.current}
              columns={columns}
              treeData
              apiRef={apiRef}
              getTreeDataPath={getTreeDataPath.current}
              groupingColDef={groupingColDef}
            />
          </ThemeProvider>
        </div>
      </SettingFormsLayout>
    </Layout>
  );
};

export default UserPermissions;
